import { DefaultError, useMutation } from "@tanstack/react-query";

import { makePayment } from "checkout-query-client/api/make-payment";
import { PayRequestPayload } from "checkout-query-client/api/types";
import { usePubSub } from "checkout-query-client/hooks/use-pub-sub";
import {
  PubSubMessage,
  PubSubNamespace,
} from "checkout-query-client/publish-subcriber/types";

import { sendCustomAction } from "datadog-loader/loader";

import {
  PayRequestErrorCode,
  PayResponse,
  PayResponse_Status,
} from "frontend-client";

export const useMakePayment = (
  checkoutSessionToken: string,
  apiHost: string,
  version: string,
  language: string
) => {
  return useMutation<PayResponse, DefaultError, PayRequestPayload, unknown>({
    retry: 0,
    mutationFn: (payload: PayRequestPayload) =>
      makePayment(apiHost, version, language, checkoutSessionToken, payload),
    onSuccess: (
      response: PayResponse,
      payRequestPayload: PayRequestPayload
    ) => {
      if (
        response.status === PayResponse_Status.SUCCESS &&
        response.lpmOrchestrationUrl
      ) {
        usePubSub().publish({
          level: "info",
          namespace: PubSubNamespace.payment,
          message: PubSubMessage.PaymentHandlingInProgress,
        });
      } else if (response.status === PayResponse_Status.SUCCESS) {
        // handle success
        usePubSub().publish({
          level: "info",
          namespace: PubSubNamespace.payment,
          message: PubSubMessage.PaymentSuccess,
          payload: { payResponse: response.toJsonString() },
        });

        sendCustomAction({
          name: "paid",
          context: {
            experienceType: payRequestPayload.experienceType,
            paymentType: payRequestPayload.paymentType,
            submitType: payRequestPayload.submitType,
            paymentMethodId: payRequestPayload.paymentMethodId,
          },
        });
      } else if (response.status === PayResponse_Status.FAILURE) {
        if (
          response.payRequestErrorCode === PayRequestErrorCode.INVALID_CVV ||
          response.payRequestErrorCode ===
            PayRequestErrorCode.INVALID_ROUTING_NUMBER
        ) {
          // prevent error from being broadcast to merchant
          // TODO add any future recoverable errors to this block, these are not broadcast on PubSub
          usePubSub().publish({
            level: "info",
            message: PubSubMessage.PaymentValidationFailed,
            namespace: PubSubNamespace.payment,
          });
        } else if (response.scaOrchestrationUrl) {
          // handle payment requirements flow (like 3ds)  continue with no pubsub publish. apps set new AppState.paymentRequirements and user does it, then pubsub is emitted
          usePubSub().publish({
            level: "info",
            namespace: PubSubNamespace.payment,
            message: PubSubMessage.PaymentValidationInProgress,
          });
        } else if (response.pbbConsentUrl) {
          usePubSub().publish({
            level: "info",
            namespace: PubSubNamespace.payment,
            message: PubSubMessage.PaymentValidationInProgress,
          });
        } else {
          // handle everything else that isn't success or 3DS related failure
          usePubSub().publish({
            level: "error",
            namespace: PubSubNamespace.payment,
            message: PubSubMessage.PaymentError,
            payload: { payResponse: response.toJsonString() },
          });
          sendCustomAction({
            name: "payment-error",
            context: {
              error: PayRequestErrorCode[response.payRequestErrorCode], // Send error code value to DD for correlation and debugging
              experienceType: payRequestPayload.experienceType,
              paymentType: payRequestPayload.paymentType,
              submitType: payRequestPayload.submitType,
              paymentMethodId: payRequestPayload.paymentMethodId,
            },
          });
        }
      }
    },
    onError: (
      _error: DefaultError,
      _variables: PayRequestPayload,
      context: unknown
    ) => {
      sendCustomAction({
        name: "payment-error",
        context: {
          error: context,
          message: PubSubMessage.PaymentError,
        },
      });
      usePubSub().publish({
        level: "error",
        namespace: PubSubNamespace.payment,
        message: PubSubMessage.PaymentError,
      });
    },
  });
};
